import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { trackAddToCart, trackRemoveFromCart } from '#/lib/analytics';
import { getWithTtl, removeWithTtl, setWithTtl } from '#/lib/expiringStorage';
import { LOGIN_FLOW_TTL_MS } from '#/config/auth';
import type { University } from '#/types';

/**
 * 장바구니 스토어 — 담은 대학 선택 상태의 SSoT (W-FO-8 /packages).
 *
 * 정적 SPA 세션 상태(Pinia) — 백엔드 `/api/fo/cart` 미연동. 실연동 시 action 이 http 호출하도록 교체.
 * 식별 키는 `universityId`(number) — API 연동 후 University 타입의 실 id 필드.
 * 헤더 뱃지(cart.count)·요약 금액은 본 스토어를 직독한다(auth.cartCount 는 deprecated).
 *
 * 가격은 보유하지 않는다 — 표시 금액은 GET /api/fo/pricing 서버 조회(DM 02:259 위변조 차단).
 * 클라 추정(resolveTierLabel) 는 009-cart-pricing-upsell 에서 제거됨.
 */
/**
 * 보류 담기 의도 sessionStorage 키 — 비로그인 담기 클릭 시 대상 universityId 를 보존한다.
 * 메모리(Pinia) 대신 sessionStorage 를 쓰는 이유: 구글 SSO 는 브라우저 전체를 리다이렉트하므로
 * (window.location.href) 메모리 상태가 휘발한다. 로컬 로그인(SPA 내 router.push)까지 단일 경로로
 * 일관 처리하기 위해 양쪽 모두 sessionStorage 를 경유한다.
 * TTL 봉투로 저장(LOGIN_FLOW_TTL_MS) — 미로그인 이탈 시 잔존을 자동 폐기해 잊힌 자동 담기를 막는다
 * ([[expiringStorage]]). 복귀지(returnUrl)와 동일 TTL([[config/auth]])로 흐름 수명을 일치시킨다.
 */
const PENDING_INTENT_KEY = 'fo.cart.pendingAddId';

/**
 * 담은 목록 영속 sessionStorage 키 — 새로고침·뒤로가기 복귀에서 장바구니를 생존시킨다(#58).
 * pendingAddId(흐름 의도)와 달리 사용자가 명시 선택한 상태라, 탭 수명 동안 유지한다.
 * 매체로 localStorage 가 아닌 sessionStorage 를 쓰는 이유: 자료 구매는 어떤 대학을 준비하는지
 * 드러내는 민감 정보 → 공용 PC 에서 탭만 닫고 떠나도 다음 사용자에게 남지 않도록 탭 종료 시 소멸시킨다.
 */
const ITEMS_KEY = 'fo.cart.items';

/**
 * 장바구니 목록 영속 TTL — 사실상 무만료(긴 상수). sessionStorage 자체가 탭 종료 시 소멸하므로
 * 탭 수명 = 실질 만료다. 흐름 상태(LOGIN_FLOW_TTL_MS 10분)와 성격이 달라 별도 상수로 둔다.
 */
const ITEMS_TTL_MS = 24 * 60 * 60 * 1000; // 24h

/**
 * 카탈로그 조회 전·실패 시에만 쓰는 담기 상한 폴백. 실제 상한은 카탈로그 합격컷 상품군의
 * `maxSelectableCount`(서버 SSoT, 079)이며 {@link useCartStore} 의 `setMaxItems` 로 주입한다.
 * 폴백이어도 서버 견적·주문 생성이 상한을 다시 검증하므로 초과분은 결제 단계에서 차단된다.
 */
export const DEFAULT_MAX_ITEMS = 10;

export const useCartStore = defineStore('cart', () => {
  /** 담은 대학 목록(선택 순서 유지). 초기값은 sessionStorage 에서 복원해 새로고침 생존(#58). */
  const items = ref<University[]>(readItems());

  /**
   * 로그인 후 자동으로 담을 대학 id — 비로그인 담기 클릭 시 설정, 복귀 후 소비(consume)되면 비운다.
   * sessionStorage 에 미러링해 SSO 전체 리다이렉트에서도 생존시킨다.
   */
  const pendingAddId = ref<number | null>(readPendingIntent());

  /** sessionStorage(TTL 봉투)에서 보류 의도를 읽는다(없거나·만료·유효하지 않으면 null). */
  function readPendingIntent(): number | null {
    const id = getWithTtl<number>(PENDING_INTENT_KEY, LOGIN_FLOW_TTL_MS);
    return typeof id === 'number' && Number.isFinite(id) ? id : null;
  }

  /**
   * sessionStorage(TTL 봉투)에서 담은 목록을 복원한다 — 배열이 아니거나·손상·만료면 빈 배열.
   * 외부에서 손댄 값 방어: 각 원소가 universityId(number)를 가진 것만 통과시킨다.
   */
  function readItems(): University[] {
    const raw = getWithTtl<University[]>(ITEMS_KEY, ITEMS_TTL_MS);
    if (!Array.isArray(raw)) return [];
    return raw.filter((u) => u && typeof u.universityId === 'number');
  }

  /** 현재 담은 목록을 sessionStorage 에 저장한다 — 변경 action 마다 호출(watch 미사용 관행). */
  function persistItems(): void {
    setWithTtl(ITEMS_KEY, items.value);
  }

  /** 담기 상한 — 카탈로그 합격컷 그룹의 maxSelectableCount 로 갱신된다(미조회 시 폴백). */
  const maxItems = ref(DEFAULT_MAX_ITEMS);

  /** 카탈로그에서 받은 상한을 반영한다(1 이상 정수만). */
  function setMaxItems(n: number | null | undefined): void {
    maxItems.value = Number.isInteger(n) && (n as number) > 0 ? (n as number) : DEFAULT_MAX_ITEMS;
  }

  /** 담은 개수 — 헤더 뱃지·티어 산정 근거. */
  const count = computed(() => items.value.length);

  /** 추가 가능 여부 — maxItems 미만이어야 담기 버튼 활성. */
  const canAddMore = computed(() => items.value.length < maxItems.value);

  /**
   * 주문 생성용 contentId 목록 — contentId 가 null 인 대학(자료 미존재)은 제외.
   * createOrder() 에 직접 전달한다.
   */
  const contentIds = computed(() =>
    items.value.map((u) => u.contentId).filter((id): id is number => typeof id === 'number'),
  );

  /** 이미 담겼는지 — 카드 토글 상태·중복 차단(universityId 기준). */
  function has(id: number): boolean {
    return items.value.some((u) => u.universityId === id);
  }

  /**
   * 담기(중복 universityId·maxItems 초과 무시).
   * @returns true=담기 성공, false=중복이거나 최대 수량 초과
   */
  function add(university: University): boolean {
    if (has(university.universityId)) return false;
    if (items.value.length >= maxItems.value) return false;
    items.value.push(university);
    persistItems();
    // 조기 return 이후라 "실제로 담긴 경우"만 계측된다(중복·최대 수량 초과는 미발화).
    trackAddToCart(university);
    return true;
  }

  /** 제거(universityId 기준). */
  function remove(id: number): void {
    // 계측 대상은 filter 이전에 잡아 둔다 — 제거 후에는 대학명·contentId 를 알 수 없다.
    const removed = items.value.find((u) => u.universityId === id);
    items.value = items.value.filter((u) => u.universityId !== id);
    persistItems();
    trackRemoveFromCart(removed);
  }

  /** 담기/해제 토글(universityId 기준). */
  function toggle(university: University): void {
    if (has(university.universityId)) remove(university.universityId);
    else add(university);
  }

  /**
   * 전체 비우기(결제 완료·로그아웃 시) — 담은 목록과 미완 보류 의도를 함께 폐기한다.
   * 로그아웃 시 이전 세션의 보류 담기가 다음 흐름으로 새지 않도록 보류 의도(sessionStorage 포함)도 정리한다.
   */
  function clear(): void {
    items.value = [];
    // 영속 스냅샷도 제거 — 로그아웃 시 이전 세션 선택이 다음 사용자/세션에 남지 않도록(누출 방지).
    removeWithTtl(ITEMS_KEY);
    consumePendingAdd();
  }

  /**
   * 보류 담기 의도 설정 — 비로그인 사용자가 담기를 누른 직후 호출.
   * 로그인 화면으로 이탈하기 전에 대상 id 를 sessionStorage 에 박아, 복귀 후 자동 담기에 쓴다.
   */
  function setPendingAdd(id: number): void {
    pendingAddId.value = id;
    setWithTtl(PENDING_INTENT_KEY, id);
  }

  /**
   * 보류 담기 의도 소비 — 로그인 복귀 후 1회 읽고 즉시 비운다(중복 자동 담기 방지).
   *
   * <p><b>소비 시점 TTL 재검증</b>: 메모리 ref 가 아니라 sessionStorage(TTL 봉투)에서 다시 읽는다. 로컬
   * 로그인은 SPA 라 스토어가 언로드되지 않아, 사용자가 로그인 화면에서 TTL(10분)을 넘겨 머문 뒤 로그인해도
   * 메모리 ref 는 살아 있다 — 그대로 반환하면 만료된 의도가 자동 실행된다. {@code readPendingIntent()}(=
   * getWithTtl)는 만료 시 sessionStorage 를 제거하고 null 을 돌려주므로, 만료 의도는 소비되지 않는다.
   * @returns 유효한(미만료) 보류 universityId, 없거나 만료면 null.
   */
  function consumePendingAdd(): number | null {
    // 메모리 ref 대신 TTL 봉투를 재검증해 읽는다(만료면 null + sessionStorage 자동 제거).
    const id = readPendingIntent();
    pendingAddId.value = null;
    removeWithTtl(PENDING_INTENT_KEY);
    return id;
  }

  return {
    items,
    count,
    maxItems,
    setMaxItems,
    canAddMore,
    contentIds,
    pendingAddId,
    has,
    add,
    remove,
    toggle,
    clear,
    setPendingAdd,
    consumePendingAdd,
  };
});
